import { useState } from "react";
import { useSubscription, useCheckout, type BillingInterval } from "@atlas/billing";
import { TIER_CONFIG, type SubscriptionTier } from "@atlas/shared";
import { config, billingApiConfig, TOKEN_TOPUP_PRICES } from "../../config";
import { CheckoutModal } from "../../components/checkout-modal";

/**
 * Format price from cents to dollars
 */
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Format token count for display
 */
function formatTokens(tokens: number): string {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(1)}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(0)}K`;
  }
  return tokens.toString();
}

/**
 * Get price ID for a tier and interval
 */
function getPriceId(tier: SubscriptionTier, interval: BillingInterval): string {
  if (tier === "starter") {
    return interval === "month"
      ? config.stripe.prices.starter.monthly
      : config.stripe.prices.starter.yearly;
  }
  if (tier === "pro") {
    return interval === "month"
      ? config.stripe.prices.pro.monthly
      : config.stripe.prices.pro.yearly;
  }
  if (tier === "byok") {
    return interval === "month"
      ? config.stripe.prices.byok.monthly
      : config.stripe.prices.byok.yearly;
  }
  return "";
}

/**
 * Billing settings page
 * - Current Plan section showing tier and interval
 * - Manage Billing button that calls initiatePortal and redirects to Stripe
 * - Token Balance section with progress bar
 * - Upgrade Plans section (if on free tier) with monthly/annual toggle
 * - Token Top-ups section with 250K, 1M, 3M options
 * - CheckoutModal for upgrade/top-up purchases
 * - Uses useSubscription and useCheckout from @atlas/billing
 * - Uses TIER_CONFIG from @atlas/shared for tier info
 */
export function BillingSettingsPage(): React.ReactElement {
  const { subscription, tokenBalance, isLoading, error, refresh } =
    useSubscription({ apiConfig: billingApiConfig });
  const { initiateCheckout, initiatePortal, isLoading: isCheckoutLoading } =
    useCheckout({ apiConfig: billingApiConfig });

  const [billingInterval, setBillingInterval] = useState<BillingInterval>("month");
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(
    null
  );
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const currentTier = subscription?.tier ?? "free";
  const tierConfig = TIER_CONFIG[currentTier];

  const handleManageBilling = async (): Promise<void> => {
    try {
      const portalUrl = await initiatePortal({
        returnUrl: window.location.href,
      });
      window.location.href = portalUrl;
    } catch (err) {
      setCheckoutError(
        err instanceof Error ? err.message : "Failed to open billing portal"
      );
    }
  };

  const handleUpgrade = async (tier: SubscriptionTier): Promise<void> => {
    try {
      setCheckoutError(null);
      const priceId = getPriceId(tier, billingInterval);
      const clientSecret = await initiateCheckout({
        priceId,
        mode: "subscription",
        successUrl: `${window.location.origin}/settings/billing?success=true`,
        cancelUrl: window.location.href,
      });
      setCheckoutClientSecret(clientSecret);
    } catch (err) {
      setCheckoutError(
        err instanceof Error ? err.message : "Failed to start checkout"
      );
    }
  };

  const handleTopUp = async (priceId: string): Promise<void> => {
    try {
      setCheckoutError(null);
      const clientSecret = await initiateCheckout({
        priceId,
        mode: "payment",
        successUrl: `${window.location.origin}/settings/billing?success=true`,
        cancelUrl: window.location.href,
      });
      setCheckoutClientSecret(clientSecret);
    } catch (err) {
      setCheckoutError(
        err instanceof Error ? err.message : "Failed to start checkout"
      );
    }
  };

  const closeCheckoutModal = (): void => {
    setCheckoutClientSecret(null);
    // Refresh subscription data after checkout
    refresh();
  };

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 py-5 max-w-3xl mx-auto">
        <h1 className="text-lg font-semibold text-text-primary mb-5">Billing</h1>
        <p className="text-sm text-text-secondary">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 py-5 max-w-3xl mx-auto">
        <h1 className="text-lg font-semibold text-text-primary mb-5">Billing</h1>
        <div className="alert-error">
          {error.message}
        </div>
      </div>
    );
  }

  // Calculate token usage percentage
  const totalTokens = tokenBalance
    ? tokenBalance.subscriptionTokens + tokenBalance.topUpTokens
    : 0;
  const monthlyAllocation = tokenBalance?.monthlyAllocation ?? 0;
  const usagePercentage =
    monthlyAllocation > 0
      ? Math.min(100, ((monthlyAllocation - totalTokens) / monthlyAllocation) * 100)
      : 0;

  return (
    <div className="px-4 sm:px-6 py-5 max-w-3xl mx-auto">
      <h1 className="text-lg font-semibold text-text-primary mb-5">Billing</h1>

      {checkoutError && (
        <div className="alert-error mb-4">
          {checkoutError}
        </div>
      )}

      {/* Current Plan Section */}
      <section className="card mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-1">Current Plan</h2>
            <p className="text-sm font-semibold text-text-primary">
              {tierConfig.name}
            </p>
            {subscription?.stripeSubscriptionId && (
              <p className="text-xs text-text-secondary mt-0.5">
                {subscription.cancelAtPeriodEnd
                  ? "Cancels at end of period"
                  : "Active subscription"}
              </p>
            )}
          </div>
          {subscription?.stripeCustomerId && (
            <button
              onClick={handleManageBilling}
              disabled={isCheckoutLoading}
              className="btn-secondary"
            >
              Manage Billing
            </button>
          )}
        </div>
      </section>

      {/* Token Balance Section */}
      <section className="card mb-4">
        <h2 className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-3">Token Balance</h2>
        <div className="flex justify-between mb-1.5 text-xs text-text-secondary">
          <span>
            {formatTokens(totalTokens)} of {formatTokens(monthlyAllocation)} remaining
          </span>
          <span>{(100 - usagePercentage).toFixed(0)}%</span>
        </div>
        <div className="h-1.5 bg-border-subtle rounded-full overflow-hidden">
          <div
            style={{
              height: "100%",
              width: `${100 - usagePercentage}%`,
              backgroundColor:
                usagePercentage > 80 ? "#ef4444" : usagePercentage > 50 ? "#f59e0b" : "#22c55e",
              transition: "width 0.3s ease",
            }}
            className="rounded-full"
          />
        </div>
        {tokenBalance && tokenBalance.topUpTokens > 0 && (
          <p className="text-text-tertiary mt-2 text-xs">
            Includes {formatTokens(tokenBalance.topUpTokens)} top-up tokens (never expire)
          </p>
        )}
      </section>

      {/* Upgrade Plans Section (only show if on free tier) */}
      {currentTier === "free" && (
        <section className="mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <h2 className="text-xs font-medium text-text-tertiary uppercase tracking-wide">Upgrade Your Plan</h2>

            {/* Billing interval toggle */}
            <div className="inline-flex bg-bg rounded-md p-0.5 border border-border self-start sm:self-auto">
              <button
                onClick={() => setBillingInterval("month")}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-all duration-150 ${
                  billingInterval === "month"
                    ? "bg-surface shadow-sm text-text-primary"
                    : "text-text-tertiary hover:text-text-secondary"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval("year")}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-all duration-150 ${
                  billingInterval === "year"
                    ? "bg-surface shadow-sm text-text-primary"
                    : "text-text-tertiary hover:text-text-secondary"
                }`}
              >
                Yearly
              </button>
            </div>
          </div>

          {/* Plan cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(["starter", "pro", "byok"] as const).map((tier) => {
              const tc = TIER_CONFIG[tier];
              const price =
                billingInterval === "month" ? tc.monthlyPrice : tc.yearlyPrice;
              const period = billingInterval === "month" ? "/mo" : "/yr";

              return (
                <div
                  key={tier}
                  className="card flex flex-col"
                >
                  <h3 className="text-sm font-semibold text-text-primary mb-0.5">{tc.name}</h3>
                  <p className="text-lg font-bold text-text-primary mb-3">
                    {formatPrice(price)}
                    <span className="text-xs font-normal text-text-tertiary ml-0.5">
                      {period}
                    </span>
                  </p>
                  <ul className="pl-4 mb-4 text-xs text-text-secondary space-y-1 flex-1">
                    {tc.features.map((feature, i) => (
                      <li key={i}>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handleUpgrade(tier)}
                    disabled={isCheckoutLoading}
                    className="btn-primary w-full"
                  >
                    Upgrade to {tc.name}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Token Top-ups Section */}
      <section className="mb-4">
        <h2 className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-1">Token Top-ups</h2>
        <p className="text-xs text-text-secondary mb-3">
          Purchase additional tokens that never expire.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(TOKEN_TOPUP_PRICES).map(([key, topup]) => (
            <div
              key={key}
              className="card text-center"
            >
              <p className="text-xs font-medium text-text-secondary mb-0.5">
                {formatTokens(topup.tokens)} tokens
              </p>
              <p className="text-sm font-bold text-text-primary mb-3">
                {formatPrice(topup.amount)}
              </p>
              <button
                onClick={() => handleTopUp(topup.priceId)}
                disabled={isCheckoutLoading}
                className="btn-secondary w-full"
              >
                Buy
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* BYOK API Key Setup (show when user is on BYOK tier) */}
      {currentTier === "byok" && (
        <section className="card bg-bg">
          <h2 className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-1">
            API Key Setup
          </h2>
          <p className="text-xs text-text-secondary mb-2">
            Configure your Anthropic API key for unlimited usage.
          </p>
          <a
            href="/settings/api-key"
            className="link text-xs font-medium"
          >
            Manage your API key &rarr;
          </a>
        </section>
      )}

      {/* Checkout Modal */}
      {checkoutClientSecret && (
        <CheckoutModal
          clientSecret={checkoutClientSecret}
          onClose={closeCheckoutModal}
        />
      )}
    </div>
  );
}
